/**
 * La colonna di destra della postazione di ricezione.
 *
 * Sta in un file suo, come gli altri pezzi del banco: `ricezione.tsx` era
 * arrivato a 689 righe, e quattro componenti dentro un file sono quattro cose
 * che si trovano solo scorrendo. Il contenitore resta là e tiene lo **stato**
 * della postazione; qui c'è solo come si disegna una riga attesa.
 */

import type { EsitoComando } from '@errebi/shared';
import { lineKey, type BenchView, type LineView } from '@errebi/domain';
import { useState } from 'react';

import type { creaBanco } from './api';
import { GarmentPicture } from './booth';
import { avvisoExpress, nomeDestinatario, nomeRiga } from './line-label';

/**
 * La colonna di destra: cosa contiene l'arrivo, e da lì si lavora.
 *
 * Con un arrivo agganciato mostra **le sue righe soltanto** — è il contenuto
 * della bolla, e sotto quel titolo la finestra intera sarebbe una cosa detta
 * per un'altra. Senza arrivo mostra le righe attese della finestra, che è ciò
 * che il banco ha davanti quando non ha ancora dichiarato niente.
 *
 * Le righe sono toccabili, come nel prototipo: chi parte dalla bolla di carta
 * ha davanti l'elenco e sceglie la riga da lavorare. Non sostituisce il codice
 * letto — quella resta la strada normale, e l'unica che fa scegliere l'ordine
 * all'anzianità invece che all'operatore (regola 2-bis). Toccare una riga è
 * dire *questa*, ed è un gesto che ha senso solo perché l'elenco è quello di un
 * arrivo dichiarato a mano.
 */
export function RigheAttese({
  v,
  fai,
  inCorso,
  rifiuti,
}: {
  v: BenchView;
  fai: (a: (b: ReturnType<typeof creaBanco>) => Promise<EsitoComando>) => () => void;
  inCorso: boolean;
  /** Bumped each time a command is refused: the fields fall back to the view. */
  rifiuti: number;
}) {
  const righe = v.arrivo ? v.righeArrivo : v.righe;
  // Express lines never go into a crate: they get their own block, prefilled.
  const righeCliente = righe.filter((r) => !r.express);
  const righeExpress = righe.filter((r) => r.express);

  return (
    <section className="righe">
      <h2 className="righe__t">{v.arrivo ? `Arrivo ${v.arrivo}` : 'Righe attese'}</h2>
      <p className="mod__hint" style={{ display: 'block', margin: '0 0 10px' }}>
        {v.arrivo
          ? 'Tocca una riga per lavorarla, oppure leggi il codice di un capo.'
          : 'Nessun arrivo dichiarato: sono le righe che la finestra aspetta.'}
      </p>
      {righe.length === 0 && <p className="mod__hint">Questo arrivo non ha righe da ricevere.</p>}
      {righeCliente.map((r) => (
        <div key={`${r.ordine}|${r.riga}`} className="riga-gruppo">
          <button
            type="button"
            // Il nome accessibile è dichiarato e non composto dal contenuto: la
            // riga porta quattro dati, e letti in fila darebbero un nome che
            // comincia col cliente — indistinguibile dai tasti delle taglie per
            // chi naviga a voce, e per i test.
            aria-label={`Lavora la riga ${r.sku}, ${nomeRiga(r)}, ${nomeDestinatario(r)}`}
            disabled={inCorso}
            onClick={fai((b) => b.agganciaCapo(r.ordine, r.riga))}
            className={
              `riga riga--${r.stato}` +
              // Merce già messa via: il bordo verde lo dice da lontano, mentre si
              // scorre l'elenco senza fermarsi a leggere i codici a destra.
              (r.cassette.length > 0 ? ' riga--incassetta' : '') +
              (v.agganciata?.ordine === r.ordine && v.agganciata.riga === r.riga
                ? ' riga--agganciata'
                : '')
            }
          >
            {/*
            Lo stesso capo della cabina luce, in piccolo — foto nostra, vista di
            catalogo, sagoma — e con lo stesso componente: due catene di ripieghi
            scritte in due posti avrebbero fatto comparire due capi diversi per
            la stessa riga. `aria-hidden` perché la riga ha già il suo nome
            dichiarato: a voce l'immagine ripeterebbe quello che è appena stato
            letto.
          */}
            <span className="riga__miniatura" aria-hidden="true">
              <GarmentPicture
                foto={r.foto}
                anteprima={r.anteprima}
                colore={r.colore}
                genere={r.genere}
                alt=""
                className="riga__mini"
              />
            </span>
            <span className="riga__testo">
              <span className="riga__cliente">{nomeDestinatario(r)}</span>
              <span className="riga__sku">
                {r.sku} · {r.colore} · {nomeRiga(r)}
              </span>
              <span className="riga__pers">
                {/* Tre destini e non due: la merce a scorta è neutra come un
                articolo non decorato, ma in magazzino si ferma invece di
                proseguire verso la spedizione. */}
                {r.aMagazzino
                  ? 'a scorta · resta in magazzino'
                  : r.neutra
                    ? 'neutro · a fine linea'
                    : r.personalizzazioni.join(' → ')}
                {/* La richiesta della foto sta nello slot del «che roba è questa»,
                accanto al destino: è lì che si guarda per capire com'è fatta
                una riga, ed è la riga neutra — quella senza vista configurata —
                a essere l'unica a cui si chiede. */}
                {r.fotoAttesa && <span className="riga__foto"> · 📷 scatta la foto</span>}
              </span>
              {/*
            Il conto a sinistra, **dove è finita** a destra.

            I codici e non il numero: «su 2 cassette» diceva quante senza dire
            quali, e la domanda che si fa a metà giro è l'altra — «questa l'ho
            già cominciata, dove l'ho messa?». In grassetto perché è la sola
            cosa di questa riga che si va a cercare con gli occhi mentre le
            mani sono occupate.
          */}
              <span className="riga__fondo">
                <span className="riga__conto">
                  {r.ricevuti}/{r.attesi} capi
                </span>
                {r.cassette.length > 0 && (
                  <span className="riga__cassette">{r.cassette.join(' ')}</span>
                )}
              </span>
            </span>
          </button>
          {v.arrivo && <RicevutiRiga r={r} fai={fai} inCorso={inCorso} rifiuti={rifiuti} />}
        </div>
      ))}
      {righeExpress.length > 0 && (
        <div className="express">
          <h3 className="express__t">Express 24h</h3>
          <p className="mod__hint" style={{ display: 'block', margin: '0 0 8px' }}>
            Va sfusa nel Modula express. I numeri sono già quelli attesi: si cambia solo se manca
            qualcosa.
          </p>
          {righeExpress.map((r) => (
            <div key={`${r.ordine}|${r.riga}`} className="express__riga">
              <span className="express__nome">
                {r.sku} · {r.colore}
              </span>
              <span className="express__avviso">{avvisoExpress(r)}</span>
              {v.arrivo && <RicevutiRiga r={r} fai={fai} inCorso={inCorso} rifiuti={rifiuti} />}
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

/**
 * The «Ricevuti» numbers of one line, one field per size.
 *
 * Distinct from the crate count: here the operator states what arrived, and the
 * closure records it. The field commits on blur or Enter — committing on every
 * keystroke would send «2» on the way to «26».
 */
function RicevutiRiga({
  r,
  fai,
  inCorso,
  rifiuti,
}: {
  r: LineView;
  fai: (a: (b: ReturnType<typeof creaBanco>) => Promise<EsitoComando>) => () => void;
  inCorso: boolean;
  rifiuti: number;
}) {
  const taglie = Object.entries(r.daRicevere);
  if (taglie.length === 0) return null;
  return (
    <div className="ricevuti">
      {taglie.map(([taglia, valore]) => (
        <CampoRicevuti
          key={taglia}
          etichetta={`Ricevuti ${r.sku} ${r.colore} ${taglia}`}
          taglia={taglia}
          valore={valore}
          atteso={r.attese[taglia] ?? valore}
          rifiuti={rifiuti}
          disabilitato={inCorso}
          onImposta={(n) => fai((b) => b.impostaRicevuti(lineKey(r.ordine, r.riga), taglia, n))()}
        />
      ))}
    </div>
  );
}

function CampoRicevuti({
  etichetta,
  taglia,
  valore,
  atteso,
  rifiuti,
  disabilitato,
  onImposta,
}: {
  etichetta: string;
  taglia: string;
  valore: number;
  atteso: number;
  rifiuti: number;
  disabilitato: boolean;
  onImposta: (n: number) => void;
}) {
  const [bozza, setBozza] = useState(String(valore));
  // The view is the truth: when it changes (a reload, a refusal) the field
  // follows. Adjusting state while rendering, not in an effect.
  const [visto, setVisto] = useState(valore);
  // A refused command leaves the view unchanged, so the value alone cannot
  // tell the field to drop the rejected draft: the refusal counter does.
  const [rifiutiVisti, setRifiutiVisti] = useState(rifiuti);
  if (visto !== valore || rifiutiVisti !== rifiuti) {
    setVisto(valore);
    setRifiutiVisti(rifiuti);
    setBozza(String(valore));
  }
  const mancano = atteso - Number(bozza);

  function conferma() {
    const n = Number(bozza);
    if (bozza.trim() === '' || !Number.isInteger(n) || n < 0) return setBozza(String(valore));
    if (n !== valore) onImposta(n);
  }

  return (
    <label className="ricevuti__campo">
      <span className="ricevuti__taglia">{taglia}</span>
      <input
        type="number"
        inputMode="numeric"
        min={0}
        aria-label={etichetta}
        value={bozza}
        disabled={disabilitato}
        onChange={(e) => setBozza(e.target.value)}
        onBlur={conferma}
        onKeyDown={(e) => {
          if (e.key === 'Enter') e.currentTarget.blur();
        }}
      />
      {bozza.trim() !== '' && mancano > 0 && <span className="ricevuti__meno">−{mancano}</span>}
    </label>
  );
}

/**
 * Closures that started and did not finish the DecoNetwork write.
 *
 * The view already carries them, so no polling: an empty list renders nothing.
 */
export function ChiusureInSospeso({
  v,
  fai,
  inCorso,
}: {
  v: BenchView;
  fai: (a: (b: ReturnType<typeof creaBanco>) => Promise<EsitoComando>) => () => void;
  inCorso: boolean;
}) {
  if (v.chiusureInSospeso.length === 0) return null;
  return (
    <section className="righe sospeso">
      <h2 className="righe__t">Bolle chiuse in sospeso</h2>
      {v.chiusureInSospeso.map((c) => (
        <div key={c.id} className="sospeso__riga">
          <span>
            <b>Bolla {c.numero}</b> · {c.motivo}
          </span>
          <button
            type="button"
            className="mod"
            disabled={inCorso}
            onClick={fai((b) => b.segnaSuDeconetwork(c.id))}
          >
            Segna su DecoNetwork
          </button>
        </div>
      ))}
    </section>
  );
}
